import React from 'react';
import {ArrowLeft, Save, Send, Loader2} from 'lucide-react';
import Button from '@/shared/components/Button';

interface OrderFooterProps {
    selectedProfileCount: number;
    manualPanelCount: number;
    selectedTestCount: number;
    orderTotal: number;
    hasTbdTests: boolean;
    canSubmit: boolean;
    isSaving: boolean;
    isSubmitting: boolean;
    isConnecting?: boolean;
    onCancel: () => void;
    onSaveDraft: () => void;
    onSubmit: () => void;
}

const OrderFooter: React.FC<OrderFooterProps> = ({
    selectedProfileCount,
    manualPanelCount,
    selectedTestCount,
    orderTotal,
    hasTbdTests,
    canSubmit,
    isSaving,
    isSubmitting,
    isConnecting,
    onCancel,
    onSaveDraft,
    onSubmit,
}) => {
    const busy = isSaving || isSubmitting;

    // Build summary parts
    const parts: string[] = [];
    if (selectedProfileCount > 0) {
        parts.push(`${selectedProfileCount} Profile${selectedProfileCount === 1 ? '' : 's'}`);
    }
    if (manualPanelCount > 0) {
        parts.push(`${manualPanelCount} extra Panel${manualPanelCount === 1 ? '' : 's'}`);
    }
    parts.push(`${selectedTestCount} total test${selectedTestCount === 1 ? '' : 's'}`);

    return (
        <div className="fixed bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white px-6 py-4 shadow-[0_-2px_6px_rgba(0,0,0,0.05)]">
            <div className="mx-auto flex max-w-4xl items-center justify-between">
                <div>
                    <div className="flex items-baseline gap-3">
                        <p className="text-sm font-semibold text-gray-900">Order Summary</p>
                        {selectedTestCount > 0 && (
                            <span className="font-mono text-sm font-bold text-gray-900">
                                ${orderTotal.toFixed(2)}{hasTbdTests && <span className="ml-1 font-semibold text-mld-warning">(TBD)</span>}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-gray-500">{parts.join(' · ')}</p>
                    {isConnecting && (
                        <p className="flex items-center gap-1 text-xs text-mld-info">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Connecting to lab system...
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={onCancel}
                        disabled={busy}
                        iconLeft={<ArrowLeft className="h-4 w-4" />}>
                        Back
                    </Button>
                    <button
                        type="button"
                        onClick={onSaveDraft}
                        disabled={busy || selectedTestCount === 0}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-mld-info/30 bg-white px-4 py-2 text-sm font-medium text-mld-info hover:bg-mld-info/5 disabled:opacity-50 transition-colors">
                        <Save className="h-4 w-4" />
                        {isSaving ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button
                        type="button"
                        onClick={onSubmit}
                        disabled={busy || !canSubmit}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
                        <Send className="h-4 w-4" />
                        {isSubmitting ? 'Submitting...' : 'Submit Order'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderFooter;
